import { isAxiosError } from 'axios';

import { conns } from '@cfg/axiosConfig.tsx';

import type { ExportFormat } from './index.ts';

export type ExportFilters = object;

export interface SerializeExportFiltersOptions {
  omitKeys?: readonly string[];
}

export interface DownloadExportOptions {
  urlName: string;
  urlArgs?: Array<string | number | boolean>;
  filters?: ExportFilters;
  format?: ExportFormat;
  defaultFileName?: string;
  omitKeys?: readonly string[];
}

export interface DownloadExportResult {
  blob: Blob;
  fileName: string;
  contentType: string | null;
}

const FORMAT_ACCEPT: Record<ExportFormat, string> = {
  csv: 'text/csv',
  xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
};

const DEFAULT_OMIT_KEYS = new Set(['page', 'page_size', 'pageSize', 'current', 'limit', 'skip']);

const hasDayjsShape = (value: unknown): value is { isValid: () => boolean; toISOString: () => string } => {
  return (
    typeof value === 'object' &&
    value !== null &&
    typeof (value as { isValid?: unknown }).isValid === 'function' &&
    typeof (value as { toISOString?: unknown }).toISOString === 'function'
  );
};

const normalizeParamValue = (value: unknown): string | null => {
  if (value === undefined || value === null) {
    return null;
  }
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value.toISOString();
  }
  if (hasDayjsShape(value)) {
    return value.isValid() ? value.toISOString() : null;
  }
  if (typeof value === 'string') {
    const trimmed = value.trim();
    return trimmed.length > 0 ? trimmed : null;
  }
  if (typeof value === 'number') {
    return Number.isFinite(value) ? String(value) : null;
  }
  if (typeof value === 'boolean') {
    return value ? 'true' : 'false';
  }
  if (typeof value === 'bigint') {
    return value.toString();
  }
  if (typeof value === 'object') {
    try {
      const serialized = JSON.stringify(value);
      return serialized && serialized !== '{}' ? serialized : null;
    } catch {
      return null;
    }
  }
  return String(value);
};

const appendParam = (params: URLSearchParams, key: string, value: unknown): void => {
  if (value === undefined || value === null) {
    return;
  }
  if (Array.isArray(value)) {
    value.forEach((entry) => appendParam(params, key, entry));
    return;
  }
  if (value instanceof Set) {
    value.forEach((entry) => appendParam(params, key, entry));
    return;
  }
  const normalized = normalizeParamValue(value);
  if (normalized !== null) {
    params.append(key, normalized);
  }
};

export const serializeExportFilters = (
  filters: ExportFilters = {},
  options: SerializeExportFiltersOptions = {},
): URLSearchParams => {
  const omitted = new Set([...DEFAULT_OMIT_KEYS, ...(options.omitKeys ?? [])]);
  const params = new URLSearchParams();
  Object.entries(filters as Record<string, unknown>).forEach(([key, value]) => {
    if (omitted.has(key)) {
      return;
    }
    appendParam(params, key, value);
  });
  return params;
};

export const parseContentDispositionFileName = (header?: string | null): string | null => {
  if (!header) {
    return null;
  }

  const encodedMatch = header.match(/filename\*=UTF-8''([^;]+)/i);
  if (encodedMatch?.[1]) {
    try {
      return decodeURIComponent(encodedMatch[1].trim().replace(/^"|"$/g, ''));
    } catch {
      return encodedMatch[1].trim().replace(/^"|"$/g, '');
    }
  }

  const quotedMatch = header.match(/filename="([^"]+)"/i);
  if (quotedMatch?.[1]) {
    return quotedMatch[1];
  }

  const plainMatch = header.match(/filename=([^;]+)/i);
  return plainMatch?.[1]?.trim().replace(/^"|"$/g, '') ?? null;
};

export const triggerBlobDownload = (blob: Blob, fileName: string): void => {
  if (typeof window === 'undefined' || typeof document === 'undefined') {
    return;
  }
  const blobUrl = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = blobUrl;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(blobUrl);
};

export const downloadExport = async ({
  urlName,
  urlArgs = [],
  filters = {},
  format = 'xlsx',
  defaultFileName,
  omitKeys,
}: DownloadExportOptions): Promise<DownloadExportResult> => {
  try {
    const params = serializeExportFilters({ ...filters, format }, { omitKeys });
    const { data, headers } = await conns<Blob>(urlName, urlArgs).get(params, {
      responseType: 'blob',
      headers: {
        Accept: FORMAT_ACCEPT[format],
      },
    });
    const contentDisposition =
      typeof headers['content-disposition'] === 'string'
        ? headers['content-disposition']
        : undefined;
    const fileName =
      parseContentDispositionFileName(contentDisposition) ??
      defaultFileName ??
      `export.${format}`;
    const contentType =
      typeof headers['content-type'] === 'string' ? headers['content-type'] : null;

    triggerBlobDownload(data, fileName);

    return {
      blob: data,
      fileName,
      contentType,
    };
  } catch (error) {
    throw new Error(await extractExportErrorMessage(error));
  }
};

const extractExportErrorMessage = async (error: unknown): Promise<string> => {
  if (isAxiosError(error)) {
    const payload = error.response?.data;
    if (payload instanceof Blob) {
      const text = await payload.text();
      try {
        const parsed = JSON.parse(text) as { detail?: unknown; message?: unknown };
        if (typeof parsed.detail === 'string') {
          return parsed.detail;
        }
        if (typeof parsed.message === 'string') {
          return parsed.message;
        }
      } catch {
        if (text.trim()) {
          return text.trim();
        }
      }
    }
    const detail = (payload as { detail?: unknown } | undefined)?.detail;
    if (typeof detail === 'string') {
      return detail;
    }
    if (error.message) {
      return error.message;
    }
  }
  if (error instanceof Error && error.message) {
    return error.message;
  }
  return 'No se pudo exportar el archivo.';
};
